import { getFlutterTime } from "@services/timeHelpers";
import {
  User,
  ControlPoint,
  EmailSent,
  EmailTemplate,
  Org,
  Project,
  ProjectHistoryEvent,
} from "types";
import { getProjectWithUpdatedControlPoint } from "./projectHelpers";
import { useProjectStore } from "@store/projectStore";
import { resolveProjectPartner } from "@utils/resolvePartner";

export const variables = {
  "@contacts": "M. Dupont, Mme. Dupont",
  "@nom_projet": "Rénovation maison Dupont",
  "@adresse_projet": "123 rue de la Paix, Paris",
  "@signature": "\nDavid Comy\nBureau Gilbert Comy",
};

export const fillVariablesInEmailTemplate = (
  template: EmailTemplate,
  project: Project,
  userData: User,
  orgData: Org,
): EmailTemplate => {
  let filledSubject = template.subject;
  let filledBody = template.body;

  // Replace @contacts with all client contacts joined
  const contactNames = project.infos.client.contacts
    .map((contact) => `${contact.prefix} ${contact.last_name}`)
    .join(", ");
  filledSubject = filledSubject.replaceAll("@contacts", contactNames || "Client");
  filledBody = filledBody.replaceAll("@contacts", contactNames || "Client");

  // Replace project name
  filledSubject = filledSubject.replaceAll("@nom_projet", project.infos.general.name);
  filledBody = filledBody.replaceAll("@nom_projet", project.infos.general.name);

  // Replace project address
  const address = [project.infos.general.address, project.infos.general.city]
    .filter(Boolean)
    .join(" ") || "Non renseignée";
  filledSubject = filledSubject.replaceAll("@adresse_projet", address);
  filledBody = filledBody.replaceAll("@adresse_projet", address);

  // Replace signature
  const signature = `${userData.infos.display_name}\n${orgData.infos.name}`;
  filledSubject = filledSubject.replaceAll("@signature", signature);
  filledBody = filledBody.replaceAll("@signature", signature);

  return {
    ...template,
    subject: filledSubject,
    body: filledBody,
  };
};

export interface EmailOption {
  label: string;
  value: string;
}

export function handleFetchEmailOptions(activeProject: Project): EmailOption[] {
  let emails: EmailOption[] = [];
  if (activeProject) {
    for (let contact of activeProject.infos.client.contacts) {
      let email = contact.email;
      if (email && !emails.some((e) => e.value === email)) {
        emails.push({
          label: `${contact.first_name + " " + contact.last_name} (${email}) (client)`,
          value: email,
        });
      }
    }

    const storePartners = useProjectStore.getState().partners;
    for (let i = 0; i < activeProject.infos.partners.length; i++) {
      let partner = resolveProjectPartner(
        activeProject.infos.partners[i],
        storePartners,
      );
      let email = partner.email || partner.company_email;
      if (email) {
        emails.push({
          label: `${partner.display_name} (${email}) (${partner.company_types?.join(", ") || "autre"})`,
          value: `partner_${i}_${email}`,
        });
      }
    }
  }

  return emails;
}

/** Extract the actual email address from an EmailOption value */
export function getEmailFromOptionValue(value: string): string {
  if (value.startsWith("partner_")) {
    return value.replace(/^partner_\d+_/, "");
  }
  return value;
}

/** Filter email options to only include partners matching the given company types.
 *  Uses the partner index encoded in the option value (partner_{index}_{email})
 *  to match the specific partner rather than searching by email across all partners
 *  (which fails when multiple partners share the same email). */
export function getMatchingPartnerOptionValues(
  options: EmailOption[],
  partners: { company_types?: string[] }[],
  companyTypes: string[],
): string[] {
  return options
    .filter((opt) => {
      if (!opt.value.startsWith("partner_")) return false;
      const match = opt.value.match(/^partner_(\d+)_/);
      if (!match) return false;
      const partnerIndex = parseInt(match[1], 10);
      const partner = partners[partnerIndex];
      if (!partner) return false;
      return partner.company_types?.some((ct) => companyTypes.includes(ct));
    })
    .map((opt) => opt.value);
}

export function handleSendEmail(
  email: EmailSent,
  activeProject: Project,
  userData: User,

  controlpoint?: ControlPoint,
): Project {
  if (!activeProject) {
    console.error("Project data or control point data is missing");
    return activeProject;
  }
  // Here you would typically make an API call to send the email
  console.log("Email to send:", email);

  // create email sent object in database
  // then add email sent as event in history event of the project*

  let new_event: ProjectHistoryEvent = {
    id: "temp_event_id_" + Date.now(),
    action: "a envoyé un email",
    date: getFlutterTime(),
    user: {
      id: userData?.id || "unknown_user",
      display_name: userData?.infos.display_name || "Unknown User",
      photo_url: userData?.customization?.photo_url || "",
    },
    email: {
      email_sent_id: email.id,
      template_id: email.email_template_id || "unknown_template",
      sent: true,
      subject: email.subject,
      body: email.body,
    },
  };

  let updatedProject = {
    ...activeProject,
    history_events: [
      ...(activeProject.history_events || []),
      {
        ...new_event,
      },
    ],
  } as Project;

  if (controlpoint) {
    let updated_control_point = {
      ...controlpoint,
      email: {
        sent: true,
        date: getFlutterTime(),
        email_sent_id: email.id,
      },
    } as ControlPoint;
    updatedProject = getProjectWithUpdatedControlPoint(
      updatedProject,
      updated_control_point,
    );
  }

  return updatedProject;
}

export function handleSendFastEmailClient(
  controlpoint: ControlPoint,
  activeProject: Project,
  userData: User,
  orgData: Org,
): { updatedProject: Project; new_email_sent: EmailSent | null } {
  // Here you would typically make an API call to send the email
  console.log(
    "Envoi rapide d'un email au client pour le point de contrôle:",
    controlpoint,
  );
  // You can use the handleSendEmail function here with pre-filled email data for the client

  // use the template associated with the control point to fill the email data and send the email
  const { emailTemplates, libraryDocuments } = useProjectStore.getState();
  let template = emailTemplates.find(
    (model) => model.id === controlpoint.associated_email_template_id,
  );
  if (!template) {
    console.error("Aucun template d'email associé à ce point de contrôle");
    return { updatedProject: activeProject, new_email_sent: null };
  }

  // Fill the template with variables (you can customize this as needed)
  let filledTemplate = fillVariablesInEmailTemplate(
    template,
    activeProject,
    userData,
    orgData,
  );

  let documents =
    libraryDocuments.filter((doc) =>
      template?.library_documents_id?.includes(doc.id),
    ) || [];

  let emailToSend: EmailSent = {
    id: "temp_email_id_" + Date.now(),
    subject: filledTemplate.subject,
    body: filledTemplate.body,
    created_at: getFlutterTime(),
    emails_to: handleFetchEmailOptions(activeProject)
      .filter((option) => option.label.includes("(client)"))
      .map((option) => option.value),
    files: documents.map((doc) => doc.file),
    email_template_id: template.id,
    sender_user_id: userData.id,
    project_id: activeProject.id,
  };

  let updated_project = handleSendEmail(
    emailToSend,
    activeProject,
    userData,
    controlpoint,
  );
  console.log(
    "Projet mis à jour après envoi de l'email rapide:",
    updated_project,
  );
  return { updatedProject: updated_project, new_email_sent: emailToSend };
}
